import { Box, Skeleton, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import type { JSX, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { SprengnetterValuationHubProsaWebModelResponseProsaWebResponse as ProsaWebResponse } from "@/api/generatedApi";
import { BalanceScaleIcon, DatabaseStackIcon, HourglassIcon, KeyIcon, PropertyGardenIcon, SaleIcon } from "@/assets";
import type { IndicativeMarketValuationApproach } from "@/helpers/indicativeMarketValuation";
import { getProsaWebDisplayFactor } from "@/helpers/prosaWeb";
import { ObjectTypeEnum } from "@/types/refineRealEstate";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  grid: {
    display: "grid",
    gap: theme.analysisLayout.sectionGap,
  },
  card: {
    display: "grid",
    gap: "4px",
    minWidth: 0,
    padding: theme.analysisLayout.cardPadding,
    borderRadius: "4px",
    backgroundColor: "white",
    border: `1px solid ${theme.semanticColors.border.subtle}`,
    alignContent: "center",
  },
  icon: {
    fontSize: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "start",
    color: theme.semanticColors.text.primary,
    marginBottom: "4px",
  },
  iconPlaceholder: {
    width: "22px",
    height: "22px",
    borderRadius: "6px",
    border: `1.5px solid ${theme.semanticColors.text.primary}`,
  },
});

interface MetricCardProps {
  icon?: ReactNode;
  label: string;
  value: string;
  dataCy?: string;
  isLoading?: boolean;
}

interface MetricItem extends MetricCardProps {
  key: string;
}

const formatEuro = (value?: number | null): string =>
  value != null ? Math.round(value).toLocaleString("de-DE") + " €" : "—";

const formatPercentFactor = (value?: number | null): string => (value ? `${value?.toLocaleString("de-DE")} %` : "-");

const formatYears = (value: number | null | undefined, unit: string): string =>
  value != null ? Math.round(value).toLocaleString("de-DE") + " " + unit : "—";

const PlaceholderIcon = (): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);

  return <Box sx={styles.iconPlaceholder} />;
};

const MetricCard = ({ icon, label, value, dataCy, isLoading = false }: MetricCardProps): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);

  return (
    <Box sx={styles.card} data-cy={dataCy}>
      <Box sx={styles.icon}>{icon ?? <PlaceholderIcon />}</Box>
      <Typography variant="body1">{label}</Typography>
      {isLoading ? (
        <Skeleton animation="wave" variant="rounded" width={90} height={24} />
      ) : (
        <Typography variant="modalHeader">{value}</Typography>
      )}
    </Box>
  );
};

interface IndicativeValuationMetricsProps {
  valuation: ProsaWebResponse | null;
  objectType?: ObjectTypeEnum;
  approach: IndicativeMarketValuationApproach;
  plotArea?: number | null;
  isLoading?: boolean;
}

export const IndicativeValuationMetrics = ({
  approach,
  objectType,
  valuation,
  plotArea,
  isLoading = false,
}: IndicativeValuationMetricsProps): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const { t } = useTranslation("IndicativeMarketValuationPanel");
  const displayFactorValue = getProsaWebDisplayFactor(valuation, objectType)?.value;

  const getMetrics = (): MetricItem[] => {
    switch (approach) {
      case "landValue": {
        const standardGroundValue =
          valuation?.factors?.standard_ground_value?.value ??
          valuation?.comparable_sales_approach?.standard_ground_value?.value;

        return [
          {
            key: "plotArea",
            label: t("metrics.plotArea"),
            value: plotArea != null ? Math.round(plotArea).toLocaleString("de-DE") + " m²" : "—",
            icon: <PropertyGardenIcon sx={{ fontSize: "30px" }} />,
            dataCy: "indicative-market-value-panel-plot-area",
          },
          {
            key: "standardGroundValue",
            label: t("metrics.standardGroundValue"),
            value:
              standardGroundValue != null ? Math.round(standardGroundValue).toLocaleString("de-DE") + " €/m²" : "—",
            icon: <KeyIcon sx={{ fontSize: "30px" }} />,
            dataCy: "indicative-market-value-panel-standard-ground-value",
          },
        ];
      }
      case "comparableSales":
        return [
          {
            key: "comparisonValue",
            label: t("breakdown.apartment.preliminaryComparisonValue"),
            value: formatEuro(valuation?.comparable_sales_approach?.preliminary_value),
            icon: <BalanceScaleIcon sx={{ fontSize: "30px" }} />,
            dataCy: "indicative-market-value-panel-comparison-value",
          },
          {
            key: "comparisonFactor",
            label: t("metrics.comparisonFactor"),
            value: displayFactorValue != null ? Math.round(displayFactorValue).toLocaleString("de-DE") : "-",
            icon: <BalanceScaleIcon sx={{ fontSize: "30px" }} />,
            dataCy: "indicative-market-value-panel-comparison-factor",
          },
        ];
      case "income":
        return [
          {
            key: "grossAnnualIncome",
            label: t("metrics.grossAnnualIncome"),
            value: formatEuro(valuation?.income_approach?.gross_annual_income),
            icon: <DatabaseStackIcon sx={{ fontSize: "30px" }} />,
            dataCy: "indicative-market-value-panel-gross-annual-income",
          },
          {
            key: "propertyYieldRate",
            label: t("metrics.propertyYieldRate"),
            value: formatPercentFactor(valuation?.income_approach?.property_yield_rate?.value),
            icon: <SaleIcon sx={{ fontSize: "30px" }} />,
            dataCy: "indicative-market-value-panel-property-yield-rate",
          },
          {
            key: "remainingUsefulLife",
            label: t("metrics.remainingUsefulLife"),
            value: formatYears(valuation?.income_approach?.remaining_useful_life, t("metrics.years")),
            icon: <HourglassIcon sx={{ fontSize: "30px" }} />,
            dataCy: "indicative-market-value-panel-remaining-useful-life",
          },
        ];
      default:
        return [
          {
            key: "buildingValue",
            label: t("metrics.buildingValue"),
            value: formatEuro(valuation?.cost_approach?.buildings_asset_value),
            icon: <DatabaseStackIcon sx={{ fontSize: "30px" }} />,
            dataCy: "indicative-market-value-panel-building-value",
          },
          {
            key: "landValue",
            label: t("metrics.landValue"),
            value:
              valuation?.factors?.standard_ground_value?.value != null
                ? Math.round(valuation.factors.standard_ground_value.value).toLocaleString("de-DE") + " €/m²"
                : "—",
            icon: <PropertyGardenIcon sx={{ fontSize: "30px" }} />,
            dataCy: "indicative-market-value-panel-land-value",
          },
          {
            key: "factor",
            label: t("metrics.factor"),
            value: displayFactorValue?.toLocaleString("de-DE") ?? "-",
            icon: <KeyIcon sx={{ fontSize: "30px" }} />,
            dataCy: "indicative-market-value-panel-factor",
          },
        ];
    }
  };

  const metrics = getMetrics();
  const sharedRemainingUsefulLife = valuation?.income_approach?.remaining_useful_life;

  if (objectType === ObjectTypeEnum.mixedUseProperty && approach === "income") {
    return (
      <Box sx={{ display: "grid", gap: "30px" }}>
        <Box sx={{ display: "grid", gap: theme.analysisLayout.compactGap }}>
          <Typography variant="body1" fontWeight={700}>
            {t("metrics.validForResidentialAndCommercial")}
          </Typography>
          <Box
            sx={{ ...styles.card, display: "flex", alignItems: "center", justifyContent: "space-between" }}
            data-cy="indicative-market-value-panel-remaining-useful-life"
          >
            <Box sx={{ display: "grid", gap: "4px" }}>
              <Box sx={styles.icon}>
                <HourglassIcon sx={{ fontSize: "30px" }} />
              </Box>
              <Typography variant="body1">{t("metrics.remainingUsefulLife")}</Typography>
            </Box>
            {isLoading ? (
              <Skeleton animation="wave" variant="rounded" width={90} height={24} />
            ) : (
              <Typography variant="modalHeader">
                {formatYears(sharedRemainingUsefulLife, t("metrics.years"))}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...styles.grid,
        gridTemplateColumns: {
          xs: "1fr",
          md: `repeat(${metrics.length}, minmax(0, 1fr))`,
        },
      }}
    >
      {metrics.map((metric) => (
        <MetricCard
          key={metric.key}
          label={metric.label}
          value={metric.value}
          icon={metric.icon}
          dataCy={metric.dataCy}
          isLoading={isLoading}
        />
      ))}
    </Box>
  );
};
